/**
 * TJR "Job Summary" formulas (FUNCTIONAL_SPEC.md §10, "Summary page formulas") reproduced for Project
 * reporting and verified against the printed Progress Operations Reports of 12 and 16 Oct 2026 for F26048.
 * Inputs are the legacy job-file records so the import path can be tested against those prints; Teslyn's own
 * entities map onto the same functions.
 *
 * Verified behaviour (tests/fixtures/f26048/tjf.json):
 *   - "Flights" counts every flight-type daily entry, including ferry-only days with no flight number.
 *   - Survey days (for the daily average) are distinct dates with survey hours > 0.
 *   - "KM Overfly" is Σ flown km − Σ planned length of the flown lines; a reflight (version digit ≠ 0) has no
 *     planned length of its own, so all of its km are overfly.
 *   - "Est KM Remaining" is the planned length of every planned line not yet flown: Σ planned geometry − Σ planned
 *     length of the flown lines. A rejected line counts as flown (its reflight, having no planned length, changes
 *     nothing), so remaining km is a flying-progress figure, not an acceptance figure.
 *   - "Est % Complete" = trunc(accepted ÷ Σ planned geometry × 100).
 */

/** TJR daily entry (record 100), the fields the summary needs. Names follow tjf_format.py. */
export interface TjrDailyEntry {
  readonly date: string; // dd/mm/yyyy
  readonly flt_num: number;
  /** 1 Flight, 2 Standby, 3 Maintenance, 4 Suspended. */
  readonly entry_type: number;
  readonly days_alloc: number;
  readonly svy_hours: number;
  readonly fuel_used_l: number;
  readonly km_flown_m: number;
  readonly km_accepted_m: number;
  readonly mobilization: boolean;
}

/** TJR line status (record 400). */
export interface TjrLineStatus {
  readonly flown: boolean;
  readonly line_number: number;
  readonly use_km: boolean;
  readonly flown_m: number;
  readonly accepted_m: number;
  readonly refly_reason: string;
}

/** Planned length in km per planned line number (base numbers only, as TJR stores its geometry). */
export type TjrPlanLengths = Readonly<Record<string, number>>;

export const ENTRY_TYPE = { flight: 1, standby: 2, maintenance: 3, suspended: 4 } as const;

export function acceptedKmPerHour(acceptedKm: number, surveyHours: number): number {
  return surveyHours > 0 ? acceptedKm / surveyHours : 0;
}

/** The spec's simple form (total − accepted); TJR's print uses plannedKmRemaining, see the header note. */
export function estKmRemaining(totalLineKm: number, acceptedKm: number): number {
  return totalLineKm - acceptedKm;
}

export function estHoursRequired(kmRemaining: number, kmPerHour: number): number {
  return kmPerHour > 0 ? Math.ceil(kmRemaining / kmPerHour) : 0;
}

export function estFuelRequired(estHours: number, fuelUsedL: number, surveyHours: number): number {
  return surveyHours > 0 ? estHours * (fuelUsedL / surveyHours) : 0;
}

export function aveDailyAcceptedKm(acceptedKm: number, surveyDays: number): number {
  return surveyDays > 0 ? acceptedKm / surveyDays : 0;
}

export function estDaysToComplete(kmRemaining: number, aveDailyKm: number): number {
  return aveDailyKm > 0 ? Math.ceil(kmRemaining / aveDailyKm) : 0;
}

/** Truncated, not rounded (TJR prints 38 for 38.4 %). */
export function estPercentComplete(acceptedKm: number, totalLineKm: number): number {
  return totalLineKm > 0 ? Math.trunc((acceptedKm / totalLineKm) * 100) : 0;
}

/** Km flown beyond the planned line geometry (reflights count in full). */
export function kmOverfly(lines: readonly TjrLineStatus[], plan: TjrPlanLengths): number {
  let km = 0;
  for (const l of lines) {
    if (!l.flown) continue;
    km += l.flown_m / 1000 - (plan[String(l.line_number)] ?? 0);
  }
  return km;
}

/** Job total line km: the planned geometry. */
export function totalLineKm(plan: TjrPlanLengths): number {
  return sum(Object.values(plan));
}

/** Planned km of lines not yet flown (a flown line counts once even if its status row appears twice). */
export function plannedKmRemaining(lines: readonly TjrLineStatus[], plan: TjrPlanLengths): number {
  const flown = new Set(lines.filter((l) => l.flown).map((l) => String(l.line_number)));
  let remaining = 0;
  for (const [k, planned] of Object.entries(plan)) if (!flown.has(k)) remaining += planned;
  return remaining;
}

export interface JobSummary {
  readonly flights: number;
  readonly surveyDays: number;
  readonly surveyHours: number;
  readonly totalLineKm: number;
  readonly totalFlownKm: number;
  readonly totalAcceptedKm: number;
  readonly estKmRemaining: number;
  readonly estPercentComplete: number;
  readonly kmOverfly: number;
  readonly daysStandby: number;
  readonly daysMaintenance: number;
  readonly daysSuspended: number;
  readonly aveDailyAcceptedKm: number;
  readonly estDaysToComplete: number;
  readonly acceptedKmPerHour: number;
  readonly estHoursRequired: number;
  readonly fuelUsedL: number;
  readonly estFuelRequired: number;
}

/**
 * The project's km figures from the reviewed line status (ADR-013), as `tjrFromTeslyn` gives them. They replace
 * TJR's daily-entry sums so that the Job Summary does not depend on which daily entries have been saved.
 */
export interface LineStatusKm {
  /** "Km flown" of every status on the decision 32 basis (`flownCreditM`). */
  readonly flownKm: number;
  /** Accepted km of reviewed, `use_km` statuses (`accepted_m`). */
  readonly acceptedKm: number;
  /** Est KM Remaining by the Teslyn rule (`planProgress`, decision 29). */
  readonly remainingKm: number;
}

/**
 * The Job Summary page from daily entries, line status and the planned line geometry.
 *
 * Without `fromLines` it is TJR's own page: total flown and accepted km are the sums of the daily entries
 * (record 100) and remaining km is TJR's flown-based `plannedKmRemaining`; the legacy `.tjf` tests use this form.
 * Teslyn passes `fromLines` (issue #11, ADR-013): flown, accepted and remaining km, and every figure derived from
 * them (% complete, average daily km, km per hour, hours, fuel and days to complete), come from line status. Flights,
 * survey days and hours, fuel used and the standby, maintenance and suspended days still come from the daily entries
 * (TJR counts a ferry-only day as a flight). Both forms agree on F26048.
 */
export function jobSummary(
  daily: readonly TjrDailyEntry[],
  lines: readonly TjrLineStatus[],
  plan: TjrPlanLengths,
  fromLines?: LineStatusKm,
): JobSummary {
  const flightEntries = daily.filter((d) => d.entry_type === ENTRY_TYPE.flight && !d.mobilization);
  const surveyDays = new Set(flightEntries.filter((d) => d.svy_hours > 0).map((d) => d.date)).size;
  const surveyHours = sum(flightEntries.map((d) => d.svy_hours));
  const totalFlownKm = fromLines?.flownKm ?? sum(flightEntries.map((d) => d.km_flown_m)) / 1000;
  const totalAcceptedKm = fromLines?.acceptedKm ?? sum(flightEntries.map((d) => d.km_accepted_m)) / 1000;
  const fuelUsedL = sum(flightEntries.map((d) => d.fuel_used_l));
  const total = totalLineKm(plan);
  const remaining = fromLines?.remainingKm ?? plannedKmRemaining(lines, plan);
  const aveDaily = aveDailyAcceptedKm(totalAcceptedKm, surveyDays);
  const perHour = acceptedKmPerHour(totalAcceptedKm, surveyHours);
  const estHours = estHoursRequired(remaining, perHour);
  const daysOf = (type: number): number =>
    sum(daily.filter((d) => d.entry_type === type).map((d) => d.days_alloc));
  return {
    flights: flightEntries.length,
    surveyDays,
    surveyHours,
    totalLineKm: total,
    totalFlownKm,
    totalAcceptedKm,
    estKmRemaining: remaining,
    estPercentComplete: estPercentComplete(totalAcceptedKm, total),
    kmOverfly: kmOverfly(lines, plan),
    daysStandby: daysOf(ENTRY_TYPE.standby),
    daysMaintenance: daysOf(ENTRY_TYPE.maintenance),
    daysSuspended: daysOf(ENTRY_TYPE.suspended),
    aveDailyAcceptedKm: aveDaily,
    estDaysToComplete: estDaysToComplete(remaining, aveDaily),
    acceptedKmPerHour: perHour,
    estHoursRequired: estHours,
    fuelUsedL,
    estFuelRequired: estFuelRequired(estHours, fuelUsedL, surveyHours),
  };
}

function sum(values: readonly number[]): number {
  return values.reduce((a, b) => a + b, 0);
}
